import type { Stripe } from '@stripe/stripe-js';
import {
  type CustomerDtoPaymentModeEnumKey,
  type PaymentMethodDtoWithIsPreferred,
  type PaymentMethodDtoWithIsPreferredPaymentMethodTypeEnumKey,
  type PaymentMethodWithDocumentsDto,
  keyValueFieldsToObject,
  retryUntilValue,
  useCustomerControllerGenerateMandateSignatureRequest,
  useCustomerControllerDeletePaymentMethod,
  useCustomerControllerUpdatePaymentMode,
  useCustomerControllerUpdatePreferredPaymentMethod,
  viewSpecificControllerListCustomerPaymentMethodsQueryOptions,
} from 'korint-sdk';
import { CreditCardIcon, EyeIcon, HandCoinsIcon, InfoIcon, LandmarkIcon } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { DocumentDownloadButton } from '@folio/components/module-elements/Documents/DocumentDownloadButton';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { formatDate } from '@folio/lib';
import { Description, DialogTitle } from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { VisuallyHidden } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';
import { Skeleton } from '../../ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { useToast } from '../../ui/use-toast';
import { PaymentMethodSetup } from './PaymentMethodsSetup';
import { PaymentMethodStatusBadge } from './PaymentMethodStatusBadge';
import { isLastChargeableMethod } from './isLastChargeableMethod';
import { PAYMENT_METHOD_WEBHOOK_RETRY } from './paymentMethodWebhookRetry';
import { RemovePaymentMethodDialog } from './RemovePaymentMethodDialog';
import { DocumentViewer, useDocumentViewer, type DocumentViewerDocument } from '../document-viewer';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Badge } from '@folio/components/ui/badge';

export type CustomerPaymentMethodsCardProps = {
  customerId: string;
  allowedDefaultPaymentMethodTypes: PaymentMethodDtoWithIsPreferredPaymentMethodTypeEnumKey[];
  /**
   * Types the backend accepts to bill the payer: `paymentMethodOnRecurringPayment` plus
   * `paymentMethodOnBrokenMethod`. Wider than `allowedDefaultPaymentMethodTypes`, and what
   * the delete guard is checked against.
   */
  chargeablePaymentMethodTypes: PaymentMethodDtoWithIsPreferredPaymentMethodTypeEnumKey[];
  allowMandateSignature?: boolean;
  stripePromise: Promise<Stripe | null>;
  customerLabel?: string;
  /** Hide the "add payment method" action when adding is not yet possible (e.g. unquoted policy). */
  canAddPaymentMethod?: boolean;
  /**
   * Overrides the add-payment-method button label. The funnel passes a step-specific call to
   * action; dashboards leave it undefined and fall back to `customerPaymentMethods.actions.add`.
   */
  addButtonLabel?: string;
  productId?: string;
  paymentMode?: CustomerDtoPaymentModeEnumKey;
  /** The product allows OFFLINE and the policy has a single payer (the backend refuses it otherwise). */
  offlineEnabled?: boolean;
  onPaymentModeUpdated?(): void;
};

export function CustomerPaymentMethodsCard({
  customerId,
  allowedDefaultPaymentMethodTypes,
  chargeablePaymentMethodTypes,
  allowMandateSignature,
  stripePromise,
  customerLabel,
  canAddPaymentMethod = true,
  addButtonLabel,
  productId,
  paymentMode,
  offlineEnabled,
  onPaymentModeUpdated,
}: CustomerPaymentMethodsCardProps) {
  const { t } = useTranslation();
  const addButtonText = addButtonLabel ?? t('customerPaymentMethods.actions.add');
  const {
    data: paymentMethods,
    status,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    ...viewSpecificControllerListCustomerPaymentMethodsQueryOptions(customerId),
  });

  const sepaPaymentMethods = useMemo(
    () => paymentMethods?.data?.filter(pm => pm.paymentMethodType === 'sepa_debit') ?? [],
    [paymentMethods],
  );
  const cardPaymentMethods = useMemo(
    () => paymentMethods?.data?.filter(pm => pm.paymentMethodType === 'card') ?? [],
    [paymentMethods],
  );

  // Deletion is card-scoped so a removal in flight disables every row's Remove
  // action until the refreshed list recalculates `canRemove` — otherwise a second
  // row could delete the now-last selectable method during the refetch window.
  const { mutate: removePaymentMethod, isPending: isRemoving } =
    useCustomerControllerDeletePaymentMethod({ mutation: { onSuccess: () => refetch() } });
  const removalInFlight = isRemoving || isRefetching;

  const { can } = useUserPermissions({ productId, actions: ['UPDATE_PAYMENT_MODE'] });
  const { mutateAsync: updatePaymentMode, isPending: isUpdatingMode } =
    useCustomerControllerUpdatePaymentMode();

  const canSelectOffline = Boolean(offlineEnabled) && can.UPDATE_PAYMENT_MODE;

  // A viewer who cannot pick off-platform must not be shown the row at all; one already on it
  // still needs to see the mode they are on, which is why they have no payment method.
  const showOffline = canSelectOffline || paymentMode === 'OFFLINE';

  const selectOffline = async () => {
    try {
      await updatePaymentMode({ customerId, data: { paymentMode: 'OFFLINE' } });
      // The backend clears the preferred method on going OFFLINE, so refetch the list too —
      // otherwise the "default" badge stays on the SEPA row until a manual refresh.
      await refetch();
      onPaymentModeUpdated?.();
    } catch {
      // The global mutation cache already toasts the userError
    }
  };

  const canRemove = (paymentMethodId: string): boolean =>
    !isLastChargeableMethod(
      paymentMethods?.data ?? [],
      paymentMethodId,
      chargeablePaymentMethodTypes,
    );

  const allDocuments = useMemo<DocumentViewerDocument[]>(() => {
    if (!paymentMethods?.data) return [];
    return paymentMethods.data.flatMap(
      pm =>
        pm.documents?.map(doc => ({
          id: doc.id,
          name: doc.name,
          signedUrl: doc.signedUrl,
          createdAt: doc.createdAt,
          type: doc.type,
          category: doc.category,
          metadata: keyValueFieldsToObject(doc.metadata),
        })) ?? [],
    );
  }, [paymentMethods]);

  const { selectedDocument, viewableDocuments, selectDocument, closeViewer } =
    useDocumentViewer(allDocuments);

  const handleViewDocument = (doc: NonNullable<PaymentMethodWithDocumentsDto['documents']>[0]) => {
    selectDocument({
      id: doc.id,
      name: doc.name,
      signedUrl: doc.signedUrl,
      createdAt: doc.createdAt,
      type: doc.type,
      category: doc.category,
      metadata: keyValueFieldsToObject(doc.metadata),
    });
  };

  const cardTitleContent = (
    <>
      {t('customerPaymentMethods.cardTitle')}
      {customerLabel && (
        <Text inline size="sm" weight="normal" tone="muted" className="ml-2">
          ({customerLabel})
        </Text>
      )}
    </>
  );

  if (status === 'pending') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{cardTitleContent}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[100px]" />
        </CardContent>
      </Card>
    );
  }

  if (status === 'error') {
    if (error && 'status' in error && error.status === 404) {
      return (
        <Card>
          <CardHeader>
            <Group justify="between" gap={0} className="w-full">
              <CardTitle>{cardTitleContent}</CardTitle>
              {canAddPaymentMethod && (
                <CustomerAddPaymentMethodDialog
                  customerId={customerId}
                  stripePromise={stripePromise}
                  handleSuccess={async () => {
                    await retryUntilValue(() => refetch({ throwOnError: true }), {
                      ...PAYMENT_METHOD_WEBHOOK_RETRY,
                      accessor: result => result?.data,
                      isValidResult: value => value?.totalCount !== paymentMethods?.totalCount,
                    });
                  }}
                  trigger={<Button size="sm">{addButtonText}</Button>}
                />
              )}
            </Group>
          </CardHeader>
          <CardContent>
            <Stack gap={6}>
              <div className="text-center text-sm text-gray-500">
                {t('customerPaymentMethods.empty')}
              </div>
              {showOffline && (
                <OfflinePaymentMethodSection
                  isDefault={paymentMode === 'OFFLINE'}
                  canSelect={canSelectOffline}
                  isPending={isUpdatingMode}
                  onSelect={selectOffline}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>{cardTitleContent}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-sm text-gray-500">
            {t('customerPaymentMethods.errors.fetch')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <Group justify="between" gap={0} className="w-full">
            <CardTitle>{cardTitleContent}</CardTitle>
            {canAddPaymentMethod && (
              <CustomerAddPaymentMethodDialog
                customerId={customerId}
                stripePromise={stripePromise}
                handleSuccess={async () => {
                  await retryUntilValue(() => refetch({ throwOnError: true }), {
                    ...PAYMENT_METHOD_WEBHOOK_RETRY,
                    accessor: result => result?.data,
                    isValidResult: value => value?.totalCount !== paymentMethods.totalCount,
                  });
                }}
                trigger={<Button size="sm">{addButtonText}</Button>}
              />
            )}
          </Group>
        </CardHeader>
        <CardContent>
          {paymentMethods.totalCount > 0 || showOffline ? (
            <Stack gap={6}>
              {sepaPaymentMethods.length > 0 && (
                <Stack gap={6} className="sm:gap-2">
                  <div className="font-semibold">{t('customerPaymentMethods.sections.sepa')}</div>
                  {sepaPaymentMethods.map(paymentMethod => (
                    <PaymentMethod
                      key={paymentMethod.id}
                      customerId={customerId}
                      paymentMethod={paymentMethod as PaymentMethodWithDocumentsDto}
                      canBeDefault={allowedDefaultPaymentMethodTypes.includes(
                        paymentMethod.paymentMethodType,
                      )}
                      canRemove={canRemove(paymentMethod.id)}
                      onRemove={() =>
                        removePaymentMethod({ customerId, paymentMethodId: paymentMethod.id })
                      }
                      isRemoving={removalInFlight}
                      allowMandateSignature={allowMandateSignature}
                      onViewDocument={handleViewDocument}
                      refetch={refetch}
                      onPaymentModeUpdated={onPaymentModeUpdated}
                    />
                  ))}
                </Stack>
              )}
              {cardPaymentMethods.length > 0 && (
                <Stack gap={6} className="sm:gap-2">
                  <div className="font-semibold">{t('customerPaymentMethods.sections.card')}</div>
                  {cardPaymentMethods.map(paymentMethod => (
                    <PaymentMethod
                      key={paymentMethod.id}
                      customerId={customerId}
                      paymentMethod={paymentMethod as PaymentMethodWithDocumentsDto}
                      canBeDefault={allowedDefaultPaymentMethodTypes.includes(
                        paymentMethod.paymentMethodType,
                      )}
                      canRemove={canRemove(paymentMethod.id)}
                      onRemove={() =>
                        removePaymentMethod({ customerId, paymentMethodId: paymentMethod.id })
                      }
                      isRemoving={removalInFlight}
                      onViewDocument={handleViewDocument}
                      refetch={refetch}
                      onPaymentModeUpdated={onPaymentModeUpdated}
                    />
                  ))}
                </Stack>
              )}
              {showOffline && (
                <OfflinePaymentMethodSection
                  isDefault={paymentMode === 'OFFLINE'}
                  canSelect={canSelectOffline}
                  isPending={isUpdatingMode}
                  onSelect={selectOffline}
                />
              )}
            </Stack>
          ) : (
            <div className="text-sm text-muted-foreground">{t('customerPaymentMethods.empty')}</div>
          )}
        </CardContent>
      </Card>

      <DocumentViewer
        productId={productId}
        document={selectedDocument}
        documents={viewableDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
      />
    </>
  );
}

function PaymentMethodLabel({ paymentMethod }: { paymentMethod: PaymentMethodDtoWithIsPreferred }) {
  const { t, i18n } = useTranslation();

  if (paymentMethod.paymentMethodType === 'sepa_debit') {
    return t('customerPaymentMethods.labels.iban', { last4: paymentMethod?.last4 });
  }

  if (paymentMethod.expirationYear && paymentMethod.expirationMonth) {
    const expiry = new Date(
      paymentMethod.expirationYear,
      paymentMethod.expirationMonth - 1,
    ).toLocaleDateString(i18n.language, {
      month: '2-digit',
      year: 'numeric',
    });
    return t('customerPaymentMethods.labels.cardWithExpiry', {
      last4: paymentMethod?.last4,
      expiry,
    });
  }

  return t('customerPaymentMethods.labels.card', { last4: paymentMethod?.last4 });
}

export type PaymentMethodProps = {
  customerId: string;
  paymentMethod: PaymentMethodWithDocumentsDto;
  canBeDefault?: boolean;
  canRemove?: boolean;
  onRemove?(): void;
  isRemoving?: boolean;
  allowMandateSignature?: boolean;
  onViewDocument?(doc: NonNullable<PaymentMethodWithDocumentsDto['documents']>[0]): void;
  refetch?(): void;
  /** Notify the parent when picking this method may have moved the customer off OFFLINE. */
  onPaymentModeUpdated?(): void;
};

export function PaymentMethod({
  customerId,
  paymentMethod,
  canBeDefault,
  canRemove,
  onRemove,
  isRemoving,
  allowMandateSignature,
  onViewDocument,
  refetch,
  onPaymentModeUpdated,
}: PaymentMethodProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutate: updatePreferredPaymentMethod, isPending: isUpdatePreferredLoading } =
    useCustomerControllerUpdatePreferredPaymentMethod({
      // Picking a method moves an OFFLINE payer back to automatic, so refresh the customer too —
      // otherwise the offline "default" badge lingers next to the new one until a manual refresh.
      mutation: {
        onSuccess: () => {
          refetch?.();
          onPaymentModeUpdated?.();
        },
      },
    });
  const { mutate: generateMandateSignature, isPending: isGenerateMandateSignatureLoading } =
    useCustomerControllerGenerateMandateSignatureRequest({
      mutation: {
        onSuccess: () => {
          toast({
            title: t('customerPaymentMethods.toast.mandateSent.title'),
            description: t('customerPaymentMethods.toast.mandateSent.description'),
          });
        },
      },
    });

  return (
    <Stack
      align="start"
      justify="start"
      gap={0}
      className="sm:items-center sm:justify-between sm:flex-row"
    >
      <Group gap={0} className="space-x-3">
        <Group gap={0}>
          <div className="p-2 bg-muted text-muted-foreground mr-2 rounded-md">
            {paymentMethod.paymentMethodType === 'sepa_debit' ? (
              <LandmarkIcon className="h-5 w-5" />
            ) : (
              <CreditCardIcon className="h-5 w-5" />
            )}
          </div>
          <PaymentMethodLabel paymentMethod={paymentMethod} />
        </Group>
        {paymentMethod.isPreferred && (
          <Badge variant="light" tone="info" size="sm">
            {t('customerPaymentMethods.badges.default')}
          </Badge>
        )}
        <PaymentMethodStatusBadge status={paymentMethod.status} />
        {paymentMethod.billingDetails && (
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <div>{paymentMethod.billingDetails.name}</div>
                <div>
                  {t('customerPaymentMethods.tooltip.addedOn', {
                    date: formatDate(new Date(paymentMethod.createdAt)),
                  })}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </Group>
      <Group gap={0}>
        {paymentMethod.documents && paymentMethod.documents.length > 0 && (
          <Group gap={1}>
            <DocumentDownloadButton
              document={{
                id: paymentMethod.documents[0].id,
                metadata: keyValueFieldsToObject(paymentMethod.documents[0].metadata),
                signedUrl: paymentMethod.documents[0].signedUrl,
                name: paymentMethod.documents[0].name,
              }}
              showButtonText={false}
            />
            <Button
              onClick={() => onViewDocument?.(paymentMethod.documents![0])}
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:text-foreground hover:bg-slate-100"
            >
              <EyeIcon className="w-4 h-4" aria-label="show-iban" />
            </Button>
          </Group>
        )}
        {paymentMethod.status === 'WAITING_MANDATE_SIGNATURE' && allowMandateSignature && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              generateMandateSignature({
                customerId,
                paymentMethodId: paymentMethod.id,
                data: {
                  deliveryMode: 'EMAIL',
                },
              })
            }
            disabled={isGenerateMandateSignatureLoading}
          >
            {t('customerPaymentMethods.actions.sendSignatureEmail')}
          </Button>
        )}
        {!paymentMethod.isPreferred && paymentMethod.status === 'ACTIVE' && canBeDefault && (
          <Button
            size="sm"
            variant="ghost"
            disabled={isUpdatePreferredLoading}
            onClick={() =>
              updatePreferredPaymentMethod({ customerId, paymentMethodId: paymentMethod.id })
            }
          >
            {t('customerPaymentMethods.actions.setAsDefault')}
          </Button>
        )}
        {canRemove && (
          <RemovePaymentMethodDialog onConfirm={() => onRemove?.()} isPending={isRemoving} />
        )}
      </Group>
    </Stack>
  );
}

function OfflinePaymentMethodSection({
  isDefault,
  canSelect,
  isPending,
  onSelect,
}: {
  isDefault: boolean;
  canSelect: boolean;
  isPending: boolean;
  onSelect(): void;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap={6} className="sm:gap-2">
      <div className="font-semibold">{t('customerPaymentMethods.sections.offline')}</div>
      <Stack
        align="start"
        justify="start"
        gap={0}
        className="sm:items-center sm:justify-between sm:flex-row"
      >
        <Group gap={0} className="space-x-3">
          <Group gap={0}>
            <div className="p-2 bg-muted text-muted-foreground mr-2 rounded-md">
              <HandCoinsIcon className="h-5 w-5" />
            </div>
            {t('customerPaymentMethods.labels.offline')}
          </Group>
          {isDefault && (
            <Badge variant="light" tone="info" size="sm">
              {t('customerPaymentMethods.badges.default')}
            </Badge>
          )}
        </Group>
        <Group gap={0}>
          {!isDefault && canSelect && (
            <Button size="sm" variant="outline" disabled={isPending} onClick={onSelect}>
              {t('customerPaymentMethods.actions.setAsDefault')}
            </Button>
          )}
        </Group>
      </Stack>
    </Stack>
  );
}

export type CustomerAddPaymentMethodDialogProps = {
  customerId: string;
  trigger: ReactNode;
  handleSuccess?(): Promise<void> | void;
  stripePromise: Promise<Stripe | null>;
};

export function CustomerAddPaymentMethodDialog({
  customerId,
  trigger,
  handleSuccess,
  stripePromise,
}: CustomerAddPaymentMethodDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <VisuallyHidden>
          <DialogTitle>{t('customerPaymentMethods.dialog.title')}</DialogTitle>
          <Description>{t('customerPaymentMethods.dialog.description')}</Description>
        </VisuallyHidden>
        <PaymentMethodSetup
          customerId={customerId}
          stripePromise={stripePromise}
          confirmSuccess={async () => {
            setOpen(false);
            await handleSuccess?.();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
